import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Siren, Plus, Clock, Activity, CheckCircle2, BedDouble } from "lucide-react";
import { toast } from "sonner";
import { TRIAGE_LEVELS, TRIAGE_COLOR, TRIAGE_LABEL, CATEGORY_LABEL, label } from "@/lib/labels";
import { Kpi, Section, Empty, Pill, PageTitle, usePatients, PatientSelect, fmtDateTime } from "@/components/services/Shared";

const STATUS_LABEL = { waiting: "En attente", in_care: "En soins", done: "Terminé" };
const OUTCOME_LABEL = { discharged: "Retour à domicile", hospitalized: "Hospitalisé", transferred: "Transféré", deceased: "Décédé" };

export default function Urgences() {
  const { user } = useApp();
  const canAct = ["hospital_admin", "doctor", "nurse", "secretary"].includes(user?.role);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", level: "jaune", complaint: "", vitals: "" });
  const [closing, setClosing] = useState(null);
  const [outcome, setOutcome] = useState("discharged");
  const patients = usePatients();

  const load = () => axios.get(`${API}/services/urgences`).then((r) => setData(r.data)).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/services/urgences`, form);
      toast.success("Patient enregistré aux urgences");
      setOpen(false); setForm({ patient_id: "", level: "jaune", complaint: "", vitals: "" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const setStatus = async (c, status) => {
    try { await axios.patch(`${API}/services/urgences/${c.id}`, { status }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const close = async () => {
    try {
      await axios.patch(`${API}/services/urgences/${closing.id}`, { status: "done", outcome });
      toast.success("Prise en charge clôturée"); setClosing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  if (!data) return <div className="text-[#5C6B61]">Chargement…</div>;
  const s = data.stats;

  return (
    <div className="space-y-6" data-testid="urgences-page">
      <PageTitle icon={Siren} title="Service des Urgences" subtitle="File d'attente triée par niveau de gravité (triage)."
        action={canAct && (
          <Button className="bg-[#C62828] hover:bg-[#A31F1F] text-white" onClick={() => setOpen(true)} data-testid="triage-new-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle admission
          </Button>
        )} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="En attente" value={s.waiting} icon={Clock} color="#E3A72F" testid="kpi-waiting" />
        <Kpi label="En soins" value={s.in_care} icon={Activity} color="#D96C4A" testid="kpi-in-care" />
        <Kpi label="Admissions aujourd'hui" value={s.today_total} icon={Siren} color="#1E4631" testid="kpi-today" />
        <Kpi label="Urgences vitales (rouge) aujourd'hui" value={s.today_rouge} icon={Siren} color="#C62828" testid="kpi-rouge" />
      </div>

      <div className="flex flex-wrap gap-2">
        {TRIAGE_LEVELS.map((t) => (
          <Pill key={t.id} color={t.color} testid={`level-count-${t.id}`}>{t.label} : {s.by_level[t.id]}</Pill>
        ))}
      </div>

      <Section title="File d'attente" icon={Clock} testid="triage-queue">
        {data.queue.length === 0 ? <Empty>Aucun patient en attente. Les urgences sont calmes.</Empty> : (
          <div className="divide-y divide-[#E5EBE7]">
            {data.queue.map((c) => (
              <div key={c.id} className="py-3 flex items-center gap-4 flex-wrap" data-testid={`triage-${c.id}`}>
                <div className="w-2 self-stretch rounded-full" style={{ background: TRIAGE_COLOR[c.level] }} />
                <div className="flex-1 min-w-[200px]">
                  <div className="font-semibold">{c.patient_name} <span className="text-xs text-[#5C6B61] font-normal">· {c.patient_age} ans · {label(CATEGORY_LABEL, c.patient_category)}</span></div>
                  <div className="text-sm text-[#1A1D1A] mt-0.5">{c.complaint}</div>
                  {c.vitals && <div className="text-xs text-[#5C6B61]">Constantes : {c.vitals}</div>}
                  <div className="text-xs text-[#5C6B61] mt-1">Arrivée {fmtDateTime(c.created_at)} · par {c.created_by_name}</div>
                </div>
                <Pill color={TRIAGE_COLOR[c.level]}>{TRIAGE_LABEL[c.level]}</Pill>
                <Pill color={c.status === "in_care" ? "#D96C4A" : "#E3A72F"}>{STATUS_LABEL[c.status]}</Pill>
                {canAct && (
                  <div className="flex gap-2">
                    {c.status === "waiting" && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(c, "in_care")} data-testid={`triage-start-${c.id}`}>
                        <Activity className="w-4 h-4 mr-1" /> Prendre en charge
                      </Button>
                    )}
                    <Button size="sm" className="bg-[#1E4631] text-white" onClick={() => { setClosing(c); setOutcome("discharged"); }} data-testid={`triage-close-${c.id}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Clôturer
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Dernières prises en charge terminées" icon={CheckCircle2}>
        {data.recent_done.length === 0 ? <Empty>Aucune prise en charge clôturée.</Empty> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-[#5C6B61]"><th className="py-2">Patient</th><th>Motif</th><th>Triage</th><th>Issue</th><th>Clôturé</th></tr></thead>
            <tbody className="divide-y divide-[#E5EBE7]">
              {data.recent_done.map((c) => (
                <tr key={c.id}><td className="py-2 font-medium">{c.patient_name}</td><td>{c.complaint}</td>
                  <td><Pill color={TRIAGE_COLOR[c.level]}>{c.level}</Pill></td>
                  <td>{c.outcome === "hospitalized" && <BedDouble className="w-3.5 h-3.5 inline mr-1" />}{OUTCOME_LABEL[c.outcome] || "—"}</td>
                  <td className="text-xs text-[#5C6B61]">{fmtDateTime(c.closed_at)}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Admission aux urgences — Triage</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="triage-form">
            <div><Label>Patient</Label><PatientSelect patients={patients} value={form.patient_id} onChange={(v) => setForm({ ...form, patient_id: v })} testid="triage-patient" /></div>
            <div>
              <Label>Niveau de gravité</Label>
              <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                <SelectTrigger data-testid="triage-level"><SelectValue /></SelectTrigger>
                <SelectContent>{TRIAGE_LEVELS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Motif de consultation</Label><Textarea required value={form.complaint} onChange={(e) => setForm({ ...form, complaint: e.target.value })} placeholder="Douleur thoracique, traumatisme, fièvre…" data-testid="triage-complaint" /></div>
            <div><Label>Constantes (optionnel)</Label><Input value={form.vitals} onChange={(e) => setForm({ ...form, vitals: e.target.value })} placeholder="TA 12/8, T° 38.5, FC 95, SpO2 97%" data-testid="triage-vitals" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#C62828] text-white" disabled={!form.patient_id} data-testid="triage-save">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!closing} onOpenChange={(v) => !v && setClosing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Clôturer — {closing?.patient_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Issue de la prise en charge</Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger data-testid="triage-outcome"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(OUTCOME_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
            {outcome === "hospitalized" && <p className="text-xs text-[#5C6B61]">Le patient sera automatiquement marqué comme hospitalisé.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClosing(null)}>Annuler</Button>
            <Button className="bg-[#1E4631] text-white" onClick={close} data-testid="triage-close-confirm">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
